import {MithrilComponent} from '@bitstillery/mithril'
import {classes, plausible_event} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'
import {Button, ButtonGroup, Dropdown, FieldCheckbox, FieldSelect, Icon, RadioGroup} from '@bitstillery/common/components/portal'
import {$t, api, logger, notifier, store} from '@bitstillery/common/app'
import {format_iso_to_fixed_date_format} from '@bitstillery/common/lib/format'
import m from '@bitstillery/mithril'
import {OfferItemFilterType} from '@bitstillery/common/api/fact2server'
import {merge_deep} from '@bitstillery/common/lib/utils'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {AvailabilityType, ExportPortalPriceListRequest, GiftBoxType} from '@bitstillery/common/api/fact2server'

import {$s} from '@/app'
import {collection, filters} from '@/components/pages/offers/offer_list'

function to_number(value: unknown): number | undefined {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value
    }
    if (typeof value === 'string' && value !== '') {
        const parsed = Number(value)
        return Number.isNaN(parsed) ? undefined : parsed
    }
    return undefined
}

function to_string_array(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return []
    }
    return value.filter((item): item is string => typeof item === 'string')
}

interface PricelistDownloadAttrs {
    model: typeof $s.offers.export
    collection?: CollectionProxy
    icon: string
    with_text?: string
    with_filters: boolean
    /** Merged onto root `.c-pricelist-download` (e.g. bar chrome styling). */
    className?: string
}

export class PricelistDownload extends MithrilComponent<PricelistDownloadAttrs> {
    data = state({
        alcohol: {
            options: [
                ['percentage', 'Percentage'],
                ['decimal', 'Decimal (0.x)'],
            ],
        },
        dropdown: {
            toggle: false,
        },
        loading: false,
        size: {
            options: [
                {label: 'l - Liters', value: 'l'},
                {label: 'cl - Centiliters', value: 'cl'},
                {label: 'ml - Milliliters', value: 'ml'},
            ],
        },
    })

    async download(vnode: m.Vnode<PricelistDownloadAttrs>) {
        const to_number_array = (value: unknown): number[] => {
            if (!Array.isArray(value)) {
                return []
            }
            return value.map((item) => to_number(item)).filter((item): item is number => item !== undefined)
        }

        const to_availability_array = (value: unknown): AvailabilityType[] => {
            if (!Array.isArray(value)) {
                return []
            }
            return value.filter((item): item is AvailabilityType => typeof item === 'string')
        }

        const to_gift_box_array = (value: unknown): GiftBoxType[] => {
            if (!Array.isArray(value)) {
                return []
            }
            return value.filter((item): item is GiftBoxType => typeof item === 'string')
        }

        const to_offer_item_type = (value: unknown): OfferItemFilterType => {
            if (value === OfferItemFilterType.NEW_ARRIVALS) return value
            if (value === OfferItemFilterType.FAVOURITES) return value
            if (value === OfferItemFilterType.SPECIALS) return value
            if (value === OfferItemFilterType.BROWSE_STOCK) return value
            return OfferItemFilterType.BROWSE_STOCK
        }

        plausible_event('pricelist_download', {
            props: {
                company_type: $s.identity.user.company_type,
                currency: $s.identity.user.currency,
                customs_status: $s.identity.user.customs_visibility,
                language: $s.identity.user.language,
                price_preference: $s.identity.user.price_preference,
            },
        })

        const price_list_data: ExportPortalPriceListRequest = {
            exclude_case_artkeys: [],
            formatting: {
                alcohol: vnode.attrs.model.alcohol.selection,
                volume: vnode.attrs.model.size.selection,
            },
            include_case_artkeys: [],
            search_terms: [],
        }

        if (vnode.attrs.with_filters) {
            const filters_query = collection.transforms.filters_to_query(filters)
            const offer_hash =
                typeof filters_query.offer_hash === 'string' && filters_query.offer_hash !== ''
                    ? filters_query.offer_hash
                    : undefined
            const refill_status = typeof filters_query.refill_status === 'string' ? filters_query.refill_status : undefined
            const brand_holder = to_number(filters_query.brand_holder)
            merge_deep(price_list_data, {
                alcohol_percentage: to_number(filters_query.alcohol_percentage),
                availabilities: to_availability_array(filters_query.availabilities),
                brand_holder,
                category_artkeys: to_number_array(filters_query.category_artkeys),
                exclude_case_artkeys: [],
                gift_box_type: to_gift_box_array(filters_query.gift_box_type),
                include_case_artkeys: [],
                offer_hash,
                offer_item_type: to_offer_item_type(filters_query.offer_item_type),
                packaging: to_string_array(filters_query.packaging),
                refill_status,
                search_terms: to_string_array(filters_query.search_terms),
                volume: to_number(filters_query.volume),
            })

            if (collection.state.selection.all) {
                price_list_data.exclude_case_artkeys = collection.state.items
                    .filter((item) => collection.state.selection.ids.includes(item.artkey))
                    .map((item) => item.case_artkey)
            } else if (collection.state.selection.ids.length > 0) {
                price_list_data.include_case_artkeys = collection.state.items
                    .filter((item) => collection.state.selection.ids.includes(item.artkey))
                    .map((item) => item.case_artkey)
            } else {
                price_list_data.include_case_artkeys = []
            }

            await this.download_price_list(vnode, price_list_data)
        } else {
            await this.download_price_list(vnode, price_list_data)
        }
    }

    async download_price_list(vnode: m.Vnode<PricelistDownloadAttrs>, price_list_data: ExportPortalPriceListRequest) {
        const vendor = process.env.MSI_TITLE.replaceAll(' ', '-').toLowerCase()
        const file_name = `${vendor}-${$t('filters.pricelist.filename').replaceAll(' ', '-')}-${format_iso_to_fixed_date_format(new Date().toISOString())}.xlsx`
        logger.info(`download ${file_name}`)
        // Store current export settings.
        void store.save()
        this.data.loading = true

        const {success} = await api.download('portal/offer-items/price-list', file_name, price_list_data, 'POST')
        if (!success) {
            notifier.notify('The download failed. Please refresh the page and try again.')
        }
        this.data.loading = false
        vnode.attrs.model.active = false
    }

    /** Left out while select-all has no total to count the selection from. */
    selection_help(): string | undefined {
        const count = collection.selection_count()
        return count === null ? undefined : $t('filters.pricelist.products_selected', {count, total: collection.state.total})
    }

    view(vnode: m.Vnode<PricelistDownloadAttrs>) {
        return (
            <Dropdown
                className={classes('c-pricelist-download', 'collection-header-action', vnode.attrs.className)}
                closeOnSelect={false}
                model={this.data.dropdown}
            >
                <Button
                    active={vnode.attrs.model.active}
                    className={'btn-download'}
                    onclick={() => {
                        // Only show checkbox selection if we use the offer list filters & collection.
                        // Otherwise we assume download all products in the collection.
                        if (vnode.attrs.with_filters) {
                            vnode.attrs.model.active = true
                        }
                    }}
                    text={vnode.attrs.with_text ? $t('filters.pricelist.download') : ''}
                    icon={vnode.attrs.icon}
                    tip={$t('filters.pricelist.download_tip')}
                    chrome={!!vnode.attrs.className}
                    type='info'
                    variant={vnode.attrs.with_text ? undefined : 'toggle'}
                />

                <div className='dropdown-body pricelist-body on-chrome'>
                    <div class='dropdown-body-header'>
                        <Icon className={classes({active: vnode.attrs.model.active})} name={'download'} type='unset' />
                        <span className='title'>{$t('filters.pricelist.download')}</span>
                    </div>
                    <div class='options'>
                        {vnode.attrs.with_filters && (
                            <FieldCheckbox
                                className='select-toggle'
                                help={this.selection_help()}
                                model={[collection.state.selection, 'all']}
                                label={$t('filters.pricelist.select_all')}
                            />
                        )}
                        <RadioGroup
                            help={$t('filters.pricelist.alcohol_format_help')}
                            label={$t('filters.pricelist.alcohol_format')}
                            model={[vnode.attrs.model.alcohol, 'selection']}
                            options={this.data.alcohol.options}
                        />
                        <FieldSelect
                            help={$t('filters.pricelist.volume_format_help')}
                            label={$t('filters.pricelist.volume_format')}
                            model={[vnode.attrs.model.size, 'selection']}
                            options={this.data.size.options}
                        />

                        <ButtonGroup>
                            <Button
                                className='btn-download a-btn-download'
                                icon='cancel'
                                onclick={(e) => {
                                    e.stopPropagation()
                                    vnode.attrs.model.active = false
                                    this.data.dropdown.toggle = false
                                }}
                                text={$t('filters.pricelist.cancel_download')}
                                tip={() => {
                                    if (this.data.loading) {
                                        return $t('filters.pricelist.download_tip_busy')
                                    }
                                    return $t('filters.pricelist.download_tip')
                                }}
                            />
                            <Button
                                className='btn-download a-btn-download'
                                disabled={this.data.loading}
                                icon='download'
                                loading={this.data.loading}
                                onclick={async () => {
                                    await this.download(vnode)
                                }}
                                text={$t('filters.pricelist.download')}
                                tip={() => {
                                    if (this.data.loading) {
                                        return $t('filters.pricelist.download_tip_busy')
                                    }
                                    return $t('filters.pricelist.download_tip')
                                }}
                                type='info'
                            />
                        </ButtonGroup>
                    </div>
                </div>
            </Dropdown>
        )
    }
}
